import { Reveal } from "@components/ui";

type ProgressStatus = "green" | "blue" | "yellow";

interface MetricRow {
  metric: string;
  target: string;
  progress: string;
  status: ProgressStatus;
}

const metrics: MetricRow[] = [
  {
    metric: "Carbon Emissions Reduction",
    target: "30% by 2030",
    progress: "On Track",
    status: "green",
  },
  {
    metric: "Smart Meter Deployment",
    target: "100% Coverage",
    progress: "85% Complete",
    status: "blue",
  },
  {
    metric: "Safety Incident Rate",
    target: "Zero Harm",
    progress: "98% Achieved",
    status: "blue",
  },
  {
    metric: "Waste Recycling Rate",
    target: "80% by 2025",
    progress: "72% Current",
    status: "yellow",
  },
  {
    metric: "Employee Training Hours",
    target: "40hrs/year",
    progress: "Exceeded",
    status: "green",
  },
];

const statusStyles: Record<ProgressStatus, string> = {
  green: "bg-[#e6f4ed] text-[#1a7a4f]",
  blue: "bg-[#e8edf7] text-[#122d63]",
  yellow: "bg-[#fef3e8] text-[#d4880f]",
};

const SustainabilityMetricsSection = () => {
  return (
    <section className="bg-[#f9f9fb] py-12 md:py-16 lg:py-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="max-w-[1368px] mx-auto">
        {/* Header */}
        <Reveal>
          <div className="text-center mb-12 lg:mb-16">
            <span className="inline-block rounded-full bg-[#e8edf7] px-4 py-1.5 text-sm font-medium text-[#122d63] mb-4">
              Performance
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-[42px] font-bold text-[#090d2c] leading-tight mb-4">
              Sustainability Metrics
            </h2>
            <p className="text-[#62636c] text-base md:text-lg max-w-[640px] mx-auto">
              Measuring our progress toward a sustainable future
            </p>
          </div>
        </Reveal>

        {/* Table Card */}
        <Reveal>
          <div className="rounded-[24px] bg-white border border-gray-200 overflow-hidden shadow-sm">
            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#122d63] text-white">
                    <th className="text-left px-6 py-4 text-sm font-semibold rounded-tl-[24px]">
                      Metric
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold">
                      Target
                    </th>
                    <th className="text-left px-6 py-4 text-sm font-semibold rounded-tr-[24px]">
                      Progress
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.map((row, index) => (
                    <tr
                      key={row.metric}
                      className={index % 2 === 1 ? "bg-[#f9f9fb]" : "bg-white"}
                    >
                      <td className="px-6 py-4 text-[#090d2c] font-medium text-sm md:text-base">
                        {row.metric}
                      </td>
                      <td className="px-6 py-4 text-[#62636c] text-sm md:text-base">
                        {row.target}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[row.status]}`}
                        >
                          {row.progress}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card view */}
            <div className="md:hidden divide-y divide-gray-100">
              <div className="bg-[#122d63] text-white px-5 py-3 rounded-t-[24px]">
                <span className="text-sm font-semibold">
                  Sustainability Metrics
                </span>
              </div>
              {metrics.map((row, index) => (
                <div
                  key={row.metric}
                  className={`px-5 py-4 ${index % 2 === 1 ? "bg-[#f9f9fb]" : "bg-white"}`}
                >
                  <p className="text-[#090d2c] font-medium text-sm mb-1">
                    {row.metric}
                  </p>
                  <p className="text-[#62636c] text-xs mb-2">
                    Target: {row.target}
                  </p>
                  <span
                    className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[row.status]}`}
                  >
                    {row.progress}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default SustainabilityMetricsSection;
